import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { throwError as observableThrowError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import type { RCRFeeScheduleResponse } from '../../shared/interfaces/rcrfeescheduleresponse.interface';
import { CdxApiGatewayHttpService } from '../../shared/services/cdx-api-gateway-http.service';
import { Endpoints } from './endpoints';
import type { ExchangeSettleDateRequest } from './interfaces/exchange-settlement-date-request';
import type { ExpandableSMBSExchangeFeeResponse } from './interfaces/ExpandableSmbsExchangeFeeResponse';
import type { RecombinationRequest } from './interfaces/ListRecombinationsRequest';
import type { RCRExchangeFeeResponse } from './interfaces/RCRExchangeFeeResponse';
import type { RCRTakedownResponse } from './interfaces/RCRTakedownResponse';

@Injectable()
export class RecombinationsService {
  private rcrExchangeFeeResponse!: RCRExchangeFeeResponse;
  private rcrFeeScheduleResponse!: RCRFeeScheduleResponse;
  private recombinationRequest!: RecombinationRequest;
  private expandableSMBSExchangeFeeResponse!: ExpandableSMBSExchangeFeeResponse;
  private recombinationAtSettlementResponse!: RCRTakedownResponse;
  private recombinationResponse: any;

  private http = inject(CdxApiGatewayHttpService);

  public getRecombinationsExchange(): Observable<object> {
    return this.http.post(
      Endpoints.LIST_RECOMBINATIONS_ENDPOINT,
      JSON.stringify(this.recombinationRequest),
    );
  }

  public calculateFee(body: any): Observable<object> {
    return this.http.post(Endpoints.EXCHANGE_CALCULATE_FEE_ENDPOINT, JSON.stringify(body));
  }

  public commitExchangeService(body: any): Observable<object> {
    return this.http.post(Endpoints.COMMIT_EXCHANGE_SERVICE, JSON.stringify(body));
  }

  public downloadExchangePDF(body: any): Observable<object> {
    return this.http.post(Endpoints.DOWNLOAD_EXCHANGE_PDF, JSON.stringify(body), null, 'text');
  }

  public getExchangeFeeSchedule(): Observable<object> {
    return this.http.post(Endpoints.EXCHANGE_FEE_SCHEDULE, JSON.stringify({}));
  }

  public checkSMBSBalances(body: any): Observable<object> {
    return this.http.post(Endpoints.VALIDATE_EXPANDABLE_EXCHANGE_ENDPOINT, JSON.stringify(body));
  }

  public getRcrTakedown(): Observable<object> {
    return this.http
      .post(Endpoints.LIST_RECOMBINATIONS_AT_TAKEDOWNS_ENDPONT, JSON.stringify({}))
      .pipe(catchError(this.handleError));
  }

  public sendNoTakedownEmail(body: any): Observable<object> {
    return this.http.post(Endpoints.NO_TAKEDOWN_FLAG_ENDPOINT, JSON.stringify(body));
  }

  public getSettledExchanges(): Observable<object> {
    return this.http
      .post(Endpoints.LIST_RECOMBINATIONS_AT_SETTLED_EXCHANGES_ENDPONT, JSON.stringify({}))
      .pipe(catchError(this.handleError));
  }

  public retrieveExchangeService(body: any): Observable<object> {
    return this.http.post(Endpoints.RETRIEVE_EXCHANGE_SERVICE, JSON.stringify(body));
  }

  public getListDealersTradersService(): Observable<object> {
    return this.http.post(Endpoints.LIST_DEALERS_TRADERS_ENDPOINT, JSON.stringify({}));
  }

  public getExchangeSettlementDateService(request: ExchangeSettleDateRequest): Observable<object> {
    return this.http.post(Endpoints.EXCHANGE_SETTLEMENT_DATE_ENDPOINT, JSON.stringify(request));
  }

  public takedownReload(body: any): Observable<object> {
    return this.http.post(Endpoints.TAKEDOWN_RELOAD_SERVICE, JSON.stringify(body));
  }

  public essoLogoutUrl(): Observable<object> {
    return this.http.post(Endpoints.ESSO_LOGOUT_ENDPOINT, JSON.stringify({}));
  }

  private handleError(error: any) {
    return observableThrowError(() => error);
  }

  private extractPDFResponse(res: any) {
    return res;
  }

  public getRCRExchangeFeeData() {
    return this.rcrExchangeFeeResponse;
  }

  public setRCRExchangeFeeResponse(rcrExchangeFeeResponseData: RCRExchangeFeeResponse) {
    this.rcrExchangeFeeResponse = rcrExchangeFeeResponseData;
  }

  public setExpandableSMBSExchangeResponse(
    smbsExchangeFeeResponseData: ExpandableSMBSExchangeFeeResponse,
  ) {
    this.expandableSMBSExchangeFeeResponse = smbsExchangeFeeResponseData;
  }

  public getExpandableSMBSExchangeResponse() {
    return this.expandableSMBSExchangeFeeResponse;
  }

  public getFeeScheduleData() {
    return this.rcrFeeScheduleResponse;
  }

  public setFeeScheduleData(rcrFeeScheduleResponseData: RCRFeeScheduleResponse) {
    this.rcrFeeScheduleResponse = rcrFeeScheduleResponseData;
  }

  public setRecombinationRequestData(recombinationRequestData: RecombinationRequest) {
    this.recombinationRequest = recombinationRequestData;
  }

  public getRecombinationRequestData(): RecombinationRequest {
    return this.recombinationRequest;
  }

  public setRecombinationResponseData(recombinationResponseData: any): void {
    this.recombinationResponse = recombinationResponseData;
  }

  public getRecombinationResponseData(): any {
    return this.recombinationResponse;
  }

  public getRCRTakedownsResponse(): RCRTakedownResponse {
    return this.recombinationAtSettlementResponse;
  }

  public setRCRTakedownsRequest(): void {}

  public updateBannerMessage(request: any): Observable<object> {
    return this.http.post(Endpoints.UPDATE_BANNER_ENDPOINT, JSON.stringify(request));
  }
}
